import apiClient, { getCsrfCookie } from './client';
import { User, ApiResponse } from '@/types';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token?: string; // Optional if using token-based auth
  /** W1.2 — the session was created but still needs a TOTP/recovery challenge. */
  requires_two_factor?: boolean;
  /** W1.2 — `security.enforce_2fa` is on and this user has no 2FA enrolled yet. */
  requires_two_factor_enrollment?: boolean;
}

/**
 * W1.2 — the auth session shape returned by login/me: the user plus whether
 * this session still needs a 2FA challenge or mandatory enrollment before it
 * can reach anything else (see EnsureTwoFactorVerified on the backend).
 */
export interface AuthSession {
  user: User;
  requiresTwoFactor: boolean;
  requiresTwoFactorEnrollment: boolean;
}

/**
 * Authenticate user with email and password
 * Fetches CSRF cookie before making the login request (required by Laravel Sanctum)
 */
export async function login(credentials: LoginCredentials): Promise<AuthSession> {
  // Get CSRF cookie first (required by Sanctum)
  await getCsrfCookie();

  const response = await apiClient.post<ApiResponse<LoginResponse>>(
    '/api/admin/login',
    credentials
  );

  const data = response.data.data!;
  return {
    user: data.user,
    requiresTwoFactor: !!data.requires_two_factor,
    requiresTwoFactorEnrollment: !!data.requires_two_factor_enrollment,
  };
}

/**
 * Logout current user and clear session
 */
export async function logout(): Promise<void> {
  await apiClient.post('/api/admin/logout');
}

/**
 * Get current authenticated user (plus 2FA session state).
 * Note: Backend returns { data: { user: {...}, requires_two_factor, requires_two_factor_enrollment } }.
 */
export async function getCurrentUser(): Promise<AuthSession> {
  const response = await apiClient.get<ApiResponse<LoginResponse>>('/api/admin/me');
  const data = response.data.data!;
  return {
    user: data.user,
    requiresTwoFactor: !!data.requires_two_factor,
    requiresTwoFactorEnrollment: !!data.requires_two_factor_enrollment,
  };
}

/**
 * Check if user is authenticated by fetching current user
 * Returns null if not authenticated
 */
export async function checkAuth(): Promise<AuthSession | null> {
  try {
    return await getCurrentUser();
  } catch (error) {
    return null;
  }
}

/**
 * Request a password reset link for the given email address.
 * Always resolves — server returns generic message to prevent enumeration.
 */
export async function forgotPassword(email: string): Promise<void> {
  await apiClient.post('/api/admin/auth/forgot-password', { email });
}

export interface ResetPasswordPayload {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}

/**
 * Reset password using the token from the reset email.
 */
export async function resetPassword(payload: ResetPasswordPayload): Promise<void> {
  await apiClient.post('/api/admin/auth/reset-password', payload);
}

const authApi = {
  login,
  logout,
  getCurrentUser,
  checkAuth,
  forgotPassword,
  resetPassword,
};

export default authApi;
